import React, { useEffect, useMemo, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    FormControl,
} from '@mui/material';
import ToastWrapper from './ToastWrapper';
import { useToastMessage } from '../hooks/useToastMessage';
import {
    OnboardDeviceHardwareConfiguration,
    Organization,
    SystemConfiguration,
    ToastType,
} from '../interfaces';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import { LinearProgressWithLabel } from './LinearProgressWithLabel';
import {
    loadOnboardDevicesHardwareConfigurations,
    selectOnboardDevicesHardwareConfigurations,
} from '../store/onboardDeviceHardwareConfigurations.reducer';
import { loadSystemConfigurations, selectSystemConfigurations } from '../store/systemConfigurations.reducer';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { loadOrganizations, selectOrganizations } from '../store/organizations.reducer';
import { Field, Form, Formik } from 'formik';
import { FormikAutocomplete } from './FormikAutocomplete';
import { createOnboardDevice } from '../api/createOnboardDevice';
import Autocomplete from '@mui/material/Autocomplete';

interface Props {
    open: boolean;
    onCreationCompleted: () => void;
    onClose: () => void;
}

interface CreateDevicesForm {
    organizationDetails?: string;
    configurationDetails: string;
    hardwareConfiguration: string;
}

function isInteger(str: string) {
    const regex = /^-?\d+$/;
    return regex.test(str);
}

export const CreateDevicesDialog = ({ open, onClose, onCreationCompleted }: Props) => {
    const dispatch = useAppDispatch();
    const [devicesCount, setDevicesCount] = useState<string>('');
    const [progress, setProgress] = useState<number>(0);
    const [loading, setLoading] = useState(false);
    const [hardwareConfigurations, setHardwareConfigurations] = useState<
        OnboardDeviceHardwareConfiguration[]
    >([]);
    const { isOpened, toastMessage, setToastMessage, handleCloseToast } = useToastMessage();

    const availableConfigurations = useAppSelector(selectSystemConfigurations);
    const availableOrganizations = useAppSelector(selectOrganizations);
    const availableHardwareConfigurations = useAppSelector(selectOnboardDevicesHardwareConfigurations);

    const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
    const [isConfigurationReadonly, setIsConfigurationReadonly] = useState<boolean>(false);

    useEffect(() => {
        dispatch(loadOrganizations());
        dispatch(loadSystemConfigurations());
        dispatch(loadOnboardDevicesHardwareConfigurations());
    }, [dispatch]);

    useEffect(() => {
        if (!availableHardwareConfigurations) {
            return;
        }

        setHardwareConfigurations(availableHardwareConfigurations);
    }, [availableHardwareConfigurations]);

    const filteredConfigurations = useMemo(() => {
        if (selectedOrganization && availableConfigurations) {
            const res = availableConfigurations.filter(
                (config) => config.systemVersion === selectedOrganization.systemVersion,
            );

            //If organization don't have selected configuration, all configuration are available
            if (res.length === 0) return availableConfigurations;

            return res;
        }
        return availableConfigurations;
    }, [selectedOrganization, availableConfigurations]);

    const handleOrganizationChange = (e: any, val: string | null, setFieldValue: any) => {
        if (!availableOrganizations) return;

        const organization = availableOrganizations.find((organization) => organization.name === val) || null;
        setSelectedOrganization(organization);

        if (val && availableConfigurations) {
            const defaultConfig = availableConfigurations.find(
                (config) => config.systemVersion === organization?.systemVersion,
            )?.systemVersion;
            if (defaultConfig) {
                setFieldValue('configurationDetails', defaultConfig);
                setIsConfigurationReadonly(true);
                return;
            }
        }
        setFieldValue('configurationDetails', undefined);
        setIsConfigurationReadonly(false);
    };

    const isOverMaximumAllowed = useMemo(() => {
        return parseInt(devicesCount) > 100;
    }, [devicesCount]);

    const handleSubmit = async (values: CreateDevicesForm, { setSubmitting }: any) => {
        try {
            setLoading(true);
            const counter = parseInt(devicesCount);
            for (let i = 1; i <= counter; i++) {
                try {
                    let organizationId;
                    if (availableOrganizations) {
                        organizationId =
                            availableOrganizations.find(
                                (organization) => organization.name === values.organizationDetails,
                            )?.id || undefined;
                    }
                    const systemVersion = values.configurationDetails;
                    const hardwareConfiguration = values.hardwareConfiguration;
                    await createOnboardDevice({ organizationId, systemVersion, hardwareConfiguration });
                } catch (e) {
                    setToastMessage({
                        message: `При створенні девайсу ${i} відбулася помилка`,
                        type: ToastType.WARNING,
                    });
                } finally {
                    setProgress(i / counter);
                }
            }
            setLoading(false);
            onCreationCompleted();
            setProgress(0);
        } catch (error) {
            setToastMessage({
                message: `Під час створення девайсів відбулася помилка`,
                type: ToastType.WARNING,
            });
        } finally {
            setSubmitting(false);
        }
    };

    const isConfigurationOptionEqualToValue = (
        option: SystemConfiguration,
        value: SystemConfiguration | null,
    ) => {
        return option.systemVersion === value?.systemVersion;
    };

    const isOrganizationOptionEqualToValue = (option: Organization, value: Organization | null) => {
        return option.id === value?.id;
    };

    const initialValues: CreateDevicesForm = {
        organizationDetails: '',
        configurationDetails: '',
        hardwareConfiguration: '',
    };

    const validateForm = (values: any) => {
        const errors: any = {};

        if (!values.configurationDetails) {
            errors.configurationDetails = 'Configuration is required';
        }

        if (!values.hardwareConfiguration) {
            errors.configurationDetails = 'Hardware configuration is required';
        }

        return errors;
    };

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>Create devices</DialogTitle>
            <DialogContent sx={{ width: 350 }}>
                <Formik onSubmit={handleSubmit} validate={validateForm} initialValues={initialValues}>
                    {({ isSubmitting, setFieldValue, errors, touched }) => (
                        <Form>
                            <Box sx={{ marginTop: 1, marginBottom: 2 }}>
                                <TextField
                                    label="Enter count"
                                    error={isOverMaximumAllowed}
                                    helperText={
                                        isOverMaximumAllowed
                                            ? 'Максимум дозволено 100 шт за одну операцію'
                                            : ''
                                    }
                                    size={'small'}
                                    fullWidth={true}
                                    value={devicesCount}
                                    inputProps={{ type: 'number' }}
                                    onChange={(event) => {
                                        if (!event.target.value) {
                                            setDevicesCount('');
                                        }

                                        if (!isInteger(event.target.value)) {
                                            event.preventDefault();
                                            return;
                                        }

                                        setDevicesCount(parseInt(event.target.value).toString());
                                    }}
                                />
                            </Box>

                            {availableOrganizations && (
                                <FormControl fullWidth sx={{ marginBottom: 2 }}>
                                    <Field
                                        as={FormikAutocomplete}
                                        size="small"
                                        name="organizationDetails"
                                        label="Organization"
                                        callBackFunc={(e: any, val: string | null) =>
                                            handleOrganizationChange(e, val, setFieldValue)
                                        }
                                        isOptionEqualToValue={isOrganizationOptionEqualToValue}
                                        options={availableOrganizations.map((option) => option.name)}
                                    />
                                </FormControl>
                            )}

                            {filteredConfigurations && (
                                <FormControl fullWidth sx={{ marginBottom: 2 }}>
                                    <Field
                                        as={FormikAutocomplete}
                                        size="small"
                                        name="configurationDetails"
                                        label="Oscar Configuration"
                                        isOptionEqualToValue={isConfigurationOptionEqualToValue}
                                        options={filteredConfigurations.map((config) => config.systemVersion)}
                                        disabled={isConfigurationReadonly}
                                    />
                                </FormControl>
                            )}

                            <FormControl fullWidth sx={{ marginBottom: 2 }}>
                                <Autocomplete
                                    onChange={(e, newValue) => {
                                        setFieldValue('hardwareConfiguration', newValue?.id);
                                    }}
                                    fullWidth
                                    disableClearable
                                    size={'small'}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            error={
                                                touched.hardwareConfiguration &&
                                                !!errors.hardwareConfiguration
                                            }
                                            helperText={
                                                touched.hardwareConfiguration && errors.hardwareConfiguration
                                            }
                                            label="Homing Software Build Configuration *"
                                        />
                                    )}
                                    isOptionEqualToValue={(newValue, oldValue) => {
                                        return newValue.id === oldValue.id;
                                    }}
                                    options={hardwareConfigurations.map((c) => {
                                        return {
                                            label: c.name,
                                            id: c.id,
                                        };
                                    })}
                                />
                            </FormControl>

                            {loading && (
                                <Box sx={{ width: '100%', marginBottom: 1 }}>
                                    <LinearProgressWithLabel value={progress * 100} />
                                </Box>
                            )}

                            <DialogActions>
                                <Button
                                    disabled={loading}
                                    size={'small'}
                                    variant="outlined"
                                    onClick={onClose}
                                    color="error"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={
                                        loading || !devicesCount || isOverMaximumAllowed || isSubmitting
                                    }
                                    size={'small'}
                                    variant="outlined"
                                    color="primary"
                                >
                                    Create
                                    {loading && (
                                        <span className="ml-2 inline-flex">
                                            <CircularProgress size={15} />
                                        </span>
                                    )}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </DialogContent>
            <ToastWrapper
                open={isOpened}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                autoHideDuration={3000}
                onClose={handleCloseToast}
                message={toastMessage?.message}
                type={toastMessage?.type}
            />
        </Dialog>
    );
};
